import { CanvasTextStyle } from 'src/service';
import { setTextStyle } from './canvas-util';

const MAX_CACHE_SIZE = 150;
const imageCacheMap: Record<string, {
    image: HTMLImageElement,
    ready: boolean,
    error: boolean,
    cache: boolean,
    lastUsed: number,
}> = {};

/** Convert external image URL to base64 data URL to avoid CORS/tainted canvas issues */
const convertImageToBase64 = async (url: string): Promise<string> => {
    try {
        // First, try direct fetch with CORS
        let response;
        try {
            response = await fetch(url, { mode: 'cors' });
        } catch (corsError) {
            // If CORS fails, use a CORS proxy
            console.log('Direct fetch failed, trying with CORS proxy');
            const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
            response = await fetch(proxyUrl);
        }
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Failed to convert image to base64:', error);
        throw error;
    }
};

/** Check if URL is external (not from same origin or data URL) */
const isExternalUrl = (url: string): boolean => {
    if (url.startsWith('data:')) return false;
    if (url.startsWith('blob:')) return false;
    if (url.startsWith('/')) return false;
    if (url.startsWith(window.location.origin)) return false;
    if (url.startsWith(process.env.PUBLIC_URL || '')) return false;
    return true;
};

/** LRU cache eviction - removes least recently used images when cache is full */
const evictLRUCache = () => {
    const entries = Object.entries(imageCacheMap);
    if (entries.length <= MAX_CACHE_SIZE) return;
    
    // Sort by lastUsed timestamp, oldest first
    entries.sort((a, b) => (a[1].lastUsed || 0) - (b[1].lastUsed || 0));
    
    // Remove oldest 20% of entries
    const toRemove = Math.floor(entries.length * 0.2);
    for (let i = 0; i < toRemove; i++) {
        delete imageCacheMap[entries[i][0]];
    }
};

/** Clear cache - useful for format changes or memory management */
export const clearImageCache = () => {
    Object.keys(imageCacheMap).forEach(key => delete imageCacheMap[key]);
};

/** Force clear cache for a specific URL to allow re-fetching with base64 conversion */
export const clearImageCacheForUrl = (url: string) => {
    delete imageCacheMap[url];
    delete imageCacheMap[`base64_${url}`];
};

export const drawFrom = async (
    ctx: CanvasRenderingContext2D | null | undefined,
    source: string,
    dx: number | ((image: HTMLImageElement) => number) = 0,
    dy: number | ((image: HTMLImageElement) => number) = 0,
) => {
    if (!ctx || source === '') return new Promise<boolean>(resolve => resolve(false));
    return new Promise<boolean>(resolve => {
        /**
         * Manually caching this image is not a good idea? Should have just let the browser do all the work. We are trying to reinvent the wheel here.
         * 
         * Still, caching image prevent constant flashing when draw, so we will keep it as long as we can until we figure a better UX.
        */
        if (imageCacheMap[source]?.ready === true) {
            const image = imageCacheMap[source].image;
            imageCacheMap[source].lastUsed = Date.now();
            const actualDX = typeof dx === 'number' ? dx : dx(image);
            const actualDY = typeof dy === 'number' ? dy : dy(image);

            ctx.drawImage(image, actualDX, actualDY);
            resolve(true);
            return;
        }
        if (imageCacheMap[source]?.error) {
            resolve(true);
            return;
        }

        const imageCached = imageCacheMap[source] && (imageCacheMap[source].cache || imageCacheMap[source].ready);
        const image = imageCached
            ? imageCacheMap[source].image
            : new Image();

        if (!imageCached) image.src = process.env.PUBLIC_URL + source;
        image.addEventListener(
            'load',
            () => {
                const actualDX = typeof dx === 'number' ? dx : dx(image);
                const actualDY = typeof dy === 'number' ? dy : dy(image);

                ctx.drawImage(image, actualDX, actualDY);
                if (imageCacheMap[source]) {
                    imageCacheMap[source].ready = true;
                    imageCacheMap[source].error = false;
                }
                resolve(true);
            },
            { once: true },
        );
        image.addEventListener(
            'error',
            () => {
                if (imageCacheMap[source]) {
                    imageCacheMap[source].error = true;
                }
                resolve(false);
            },
            { once: true },
        );

        if (!imageCached) {
            evictLRUCache();
            imageCacheMap[source] = {
                image: image,
                ready: false,
                error: false,
                cache: true,
                lastUsed: Date.now(),
            };
        }
    });
};
export const drawAsset = async (
    ctx: CanvasRenderingContext2D | null | undefined,
    source: string,
    dx: number | ((image: HTMLImageElement) => number) = 0,
    dy: number | ((image: HTMLImageElement) => number) = 0,
) => {
    return await drawFrom(
        ctx,
        '/asset/image/' + source,
        dx, dy,
    );
};

const normalizeDxy = (
    image: HTMLImageElement,
    dw?: number | ((image: HTMLImageElement) => number),
    dh?: number | ((image: HTMLImageElement) => number),
) => {
    const { naturalWidth, naturalHeight } = image;
    const baseDW = typeof dw === 'number' ? dw : dw?.(image);
    const baseDH = typeof dh === 'number' ? dh : dh?.(image);
    let actualDW = naturalWidth;
    let actualDH = naturalHeight;

    if (typeof baseDH !== 'number' && typeof baseDW === 'number') {
        actualDW = baseDW;
        actualDH = actualDW * naturalHeight / naturalWidth;
    } else if (typeof baseDH === 'number' && typeof baseDW !== 'number') {
        actualDH = baseDH;
        actualDW = actualDH * naturalWidth / naturalHeight;
    } else if (typeof baseDH === 'number' && typeof baseDW === 'number') {
        actualDW = baseDW;
        actualDH = baseDH;
    }

    return { actualDH, actualDW };
};
export const drawFromWithSize = async (
    ctx: CanvasRenderingContext2D | null | undefined,
    source: string,
    dx: number | ((image: HTMLImageElement) => number),
    dy: number | ((image: HTMLImageElement) => number),
    dw?: number | ((image: HTMLImageElement) => number),
    dh?: number | ((image: HTMLImageElement) => number),
    sx?: undefined | number | ((image: HTMLImageElement) => number),
    sy?: undefined | number | ((image: HTMLImageElement) => number),
    sw?: undefined | number | ((image: HTMLImageElement) => number),
    sh?: undefined | number | ((image: HTMLImageElement) => number),
    option?: {
        cache?: boolean,
        overwrite?: boolean,
        internalImage?: boolean,
        crossorigin?: 'anonymous' | 'with-credential',
    },
) => {
    const {
        cache = true,
        internalImage = true,
        overwrite = false,
        crossorigin,
    } = option ?? {};
    if (!ctx || source === '') return new Promise<boolean>(resolve => resolve(false));
    
    // Convert external images to base64 to avoid tainted canvas
    let actualSource = source;
    const isExternal = !internalImage && isExternalUrl(source);
    const cacheKey = isExternal ? `base64_${source}` : source;
    
    // For external images, always try to convert to base64
    if (isExternal && !source.startsWith('data:')) {
        console.log('[IMAGE] External image detected:', source);
        console.log('[IMAGE] Cache key:', cacheKey);
        console.log('[IMAGE] Is cached?:', imageCacheMap[cacheKey]?.ready);
        
        // Check if we have a base64 version cached
        if (imageCacheMap[cacheKey]?.ready === true) {
            console.log('[IMAGE] Using cached base64 version');
            // Use cached base64 version
            return new Promise<boolean>(resolve => {
                const image = imageCacheMap[cacheKey].image;
                imageCacheMap[cacheKey].lastUsed = Date.now();
                const actualDX = typeof dx === 'number' ? dx : dx(image);
                const actualDY = typeof dy === 'number' ? dy : dy(image);
                const actualSX = typeof sx === 'number' ? sx : sx?.(image);
                const actualSY = typeof sy === 'number' ? sy : sy?.(image);
                const actualSW = typeof sw === 'number' ? sw : sw?.(image);
                const actualSH = typeof sh === 'number' ? sh : sh?.(image);
                const { actualDH, actualDW } = normalizeDxy(image, dw, dh);

                if (
                    typeof actualSX === 'number'
                    && typeof actualSY === 'number'
                    && typeof actualSW === 'number'
                    && typeof actualSH === 'number'
                ) {
                    ctx.drawImage(image, actualSX, actualSY, actualSW, actualSH, actualDX, actualDY, actualDW, actualDH);
                } else {
                    ctx.drawImage(image, actualDX, actualDY, actualDW, actualDH);
                }
                resolve(true);
            });
        }
        
        // Try to convert to base64
        try {
            console.log('[IMAGE] Converting external image to base64:', source);
            actualSource = await convertImageToBase64(source);
            console.log('[IMAGE] Successfully converted to base64, length:', actualSource.length);
        } catch (error) {
            console.error('[IMAGE] Failed to convert image to base64, canvas will be tainted:', error);
            // Fall back to original URL - canvas will be tainted
            actualSource = source;
        }
    }
    
    return new Promise<boolean>(resolve => {
        /** Check `drawFrom` comment for disable reasons */
        if (imageCacheMap[cacheKey]?.ready === true) {
            const image = imageCacheMap[cacheKey].image;
            imageCacheMap[cacheKey].lastUsed = Date.now();
            const actualDX = typeof dx === 'number' ? dx : dx(image);
            const actualDY = typeof dy === 'number' ? dy : dy(image);
            const actualSX = typeof sx === 'number' ? sx : sx?.(image);
            const actualSY = typeof sy === 'number' ? sy : sy?.(image);
            const actualSW = typeof sw === 'number' ? sw : sw?.(image);
            const actualSH = typeof sh === 'number' ? sh : sh?.(image);
            const { actualDH, actualDW } = normalizeDxy(image, dw, dh);

            if (
                typeof actualSX === 'number'
                && typeof actualSY === 'number'
                && typeof actualSW === 'number'
                && typeof actualSH === 'number'
            ) {
                ctx.drawImage(image, actualSX, actualSY, actualSW, actualSH, actualDX, actualDY, actualDW, actualDH);
            } else {
                ctx.drawImage(image, actualDX, actualDY, actualDW, actualDH);
            }
            resolve(true);
            return;
        }
        if (imageCacheMap[cacheKey]?.error && overwrite === false) {
            resolve(true);
            return;
        }

        const imageCached = overwrite
            ? false
            : imageCacheMap[cacheKey] && (imageCacheMap[cacheKey].cache || imageCacheMap[cacheKey].ready);
        const image = imageCached
            ? imageCacheMap[cacheKey].image
            : new Image();

        // Don't set crossOrigin for data URLs or if we're using base64
        if (crossorigin && !isExternal && !actualSource.startsWith('data:')) {
            image.crossOrigin = crossorigin;
        }
        
        if (!imageCached) {
            image.src = actualSource.startsWith('data:') 
                ? actualSource 
                : internalImage
                    ? process.env.PUBLIC_URL + source
                    : actualSource;
        }
        image.addEventListener(
            'load',
            () => {
                const actualDX = typeof dx === 'number' ? dx : dx(image);
                const actualDY = typeof dy === 'number' ? dy : dy(image);
                const actualSX = typeof sx === 'number' ? sx : sx?.(image);
                const actualSY = typeof sy === 'number' ? sy : sy?.(image);
                const actualSW = typeof sw === 'number' ? sw : sw?.(image);
                const actualSH = typeof sh === 'number' ? sh : sh?.(image);
                const { actualDH, actualDW } = normalizeDxy(image, dw, dh);
    
                if (
                    typeof actualSX === 'number'
                    && typeof actualSY === 'number'
                    && typeof actualSW === 'number'
                    && typeof actualSH === 'number'
                ) {
                    ctx.drawImage(image, actualSX, actualSY, actualSW, actualSH, actualDX, actualDY, actualDW, actualDH);
                } else {
                    ctx.drawImage(image, actualDX, actualDY, actualDW, actualDH);
                }
                if (imageCacheMap[cacheKey]) {
                    imageCacheMap[cacheKey].ready = true;
                    imageCacheMap[cacheKey].error = false;
                }
                resolve(true);
            },
            { once: true },
        );
        image.addEventListener(
            'error',
            () => {
                if (imageCacheMap[cacheKey]) {
                    imageCacheMap[cacheKey].error = true;
                }
                resolve(false);
            },
            { once: true },
        );

        if (cache && !imageCached) {
            evictLRUCache();
            imageCacheMap[cacheKey] = {
                image: image,
                ready: false,
                error: false,
                cache: true,
                lastUsed: Date.now(),
            };
        }
    });
};
export const drawAssetWithSize: typeof drawFromWithSize = async (
    ctx,
    source,
    dx, dy,
    dw, dh,
    sx, sy,
    sw, sh,
) => {
    return await drawFromWithSize(
        ctx,
        '/asset/image/' + source,
        dx, dy,
        dw, dh,
        sx, sy,
        sw, sh,
    );
};
export const drawFromWithSizeAndFallback: typeof drawFromWithSize = async (...param) => {
    const [
        ctx,
        source,
        dx,
        dy,
        dw, dh,
        sx, sy,
        sw, sh,
        option,
    ] = [...param];
    const result = await drawFromWithSize(
        ctx,
        source,
        dx,
        dy,
        dw, dh,
        sx, sy,
        sw, sh,
        option,
    );

    /**
     * If we fail to get the image with `anonymous` crossorigin, try again with unknown crossorigin, a.k.a remove the attribute entirely.
     * 
     * Give up otherwise, remember images marked with `error` will never be queried again by this method. 
     * */
    if (result === false && option?.crossorigin === 'anonymous') {
        const newOption = { ...option, overwrite: true };
        delete newOption.crossorigin;

        return await drawFromWithSize(
            ctx,
            source,
            dx,
            dy,
            dw, dh,
            sx, sy,
            sw, sh,
            newOption,
        );
    }
    return result;
};

// Canvas pool for drawWithStyle to avoid repeated canvas creation
const canvasPool: HTMLCanvasElement[] = [];
const MAX_POOL_SIZE = 5;

const getPooledCanvas = (width: number, height: number): HTMLCanvasElement => {
    let canvas = canvasPool.pop();
    if (!canvas) {
        canvas = document.createElement('canvas');
    }
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

const returnToPool = (canvas: HTMLCanvasElement) => {
    if (canvasPool.length < MAX_POOL_SIZE) {
        canvasPool.push(canvas);
    }
};

export const drawWithStyle = async (
    canvas: HTMLCanvasElement,
    source: string,
    dx: number, dy: number,
    sw: number, sh: number,
    globalScale: number,
    style?: CanvasTextStyle,
) => {
    const ctx = canvas.getContext('2d');
    const clonedCanvas = getPooledCanvas(sw, sh);
    const clonedCtx = clonedCanvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;

    if (!clonedCtx || !ctx) {
        returnToPool(clonedCanvas);
        return;
    }

    /**
     * * First, we clone a canvas with the same size of the destination one.
     * * Then, we fill the solid color from custom style into it, now the canvas is colored.
     * * After that, we draw the asset into the colored canvas, with destination-in mode, this means the asset will only draw on the colored area (opacity factored). This way, we effectively ignore the original color from the asset, and only use the alpha channel.
     * 
     * Needless to say this will not work very well with multi-colored asset.
     * */
    await drawAssetWithSize(clonedCtx, source, 0, 0, sw, sh);
    if (style?.color) {
        clonedCtx.fillStyle = style.color;
        clonedCtx.fillRect(0, 0, sw, sh);
        clonedCtx.globalCompositeOperation = 'destination-in';
    }
    await drawAssetWithSize(clonedCtx, source, 0, 0, sw, sh);
    clonedCtx.globalCompositeOperation = 'source-over';

    /** After that, we draw the cloned canvas back into the main one, and apply corresponding shadow if needed. */
    const resetMainCanvasStyle = setTextStyle({ ctx, ...style, globalScale });
    ctx.drawImage(clonedCanvas, dx, dy);
    resetMainCanvasStyle();
    
    returnToPool(clonedCanvas);
};